import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Chip, Skeleton, Alert,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Button, Collapse, Grid, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Snackbar,
} from '@mui/material';
import {
  Search, Edit, Delete, Add, CameraAlt, Assignment, Person, History,
  FilterList, Download, Close,
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import EmptyState from '../components/EmptyState';

// Action-Konfiguration: Icon, Farbe, Label pro Aktion-Typ
const ACTION_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  TREE_CREATED:    { icon: <Add sx={{ fontSize: 16 }} />,        color: '#4a7c59', label: 'Baum erstellt' },
  TREE_UPDATED:    { icon: <Edit sx={{ fontSize: 16 }} />,       color: '#1565c0', label: 'Baum bearbeitet' },
  TREE_DELETED:    { icon: <Delete sx={{ fontSize: 16 }} />,     color: '#c62828', label: 'Baum gelöscht' },
  TREE_BULK_DELETED: { icon: <Delete sx={{ fontSize: 16 }} />,   color: '#c62828', label: 'Bäume gelöscht (Bereich)' },
  TREE_BULK_FELLED:  { icon: <Edit sx={{ fontSize: 16 }} />,     color: '#8d6e63', label: 'Bäume gefällt (Bereich)' },
  CONTROL_CREATED: { icon: <Assignment sx={{ fontSize: 16 }} />, color: '#4a7c59', label: 'Kontrolle erstellt' },
  DAMAGE_CREATED:  { icon: <Add sx={{ fontSize: 16 }} />,        color: '#e65100', label: 'Schaden erfasst' },
  DAMAGE_UPDATED:  { icon: <Edit sx={{ fontSize: 16 }} />,       color: '#ff9800', label: 'Schaden aktualisiert' },
  DAMAGE_DELETED:  { icon: <Delete sx={{ fontSize: 16 }} />,     color: '#c62828', label: 'Schaden gelöscht' },
  MEASURE_CREATED: { icon: <Add sx={{ fontSize: 16 }} />,        color: '#1565c0', label: 'Maßnahme erstellt' },
  MEASURE_UPDATED: { icon: <Edit sx={{ fontSize: 16 }} />,       color: '#2196f3', label: 'Maßnahme aktualisiert' },
  MEASURE_DELETED: { icon: <Delete sx={{ fontSize: 16 }} />,     color: '#c62828', label: 'Maßnahme gelöscht' },
  PHOTO_UPLOADED:  { icon: <CameraAlt sx={{ fontSize: 16 }} />,  color: '#7b1fa2', label: 'Foto hochgeladen' },
  PHOTO_DELETED:   { icon: <Delete sx={{ fontSize: 16 }} />,     color: '#c62828', label: 'Foto gelöscht' },
  USER_CREATED:    { icon: <Person sx={{ fontSize: 16 }} />,     color: '#4a7c59', label: 'Benutzer erstellt' },
  USER_UPDATED:    { icon: <Edit sx={{ fontSize: 16 }} />,       color: '#1565c0', label: 'Benutzer bearbeitet' },
  USER_DELETED:    { icon: <Delete sx={{ fontSize: 16 }} />,     color: '#c62828', label: 'Benutzer gelöscht' },
  LV_CREATED:      { icon: <Assignment sx={{ fontSize: 16 }} />, color: '#4a7c59', label: 'LV erstellt' },
  LV_UPDATED:      { icon: <Edit sx={{ fontSize: 16 }} />,       color: '#1565c0', label: 'LV bearbeitet' },
  LV_DELETED:      { icon: <Delete sx={{ fontSize: 16 }} />,     color: '#c62828', label: 'LV gelöscht' },
  CSV_IMPORT:      { icon: <Add sx={{ fontSize: 16 }} />,        color: '#4a7c59', label: 'CSV-Import' },
};

const ENTITY_LABELS: Record<string, string> = {
  tree: 'Baum',
  damage: 'Schaden',
  measure: 'Maßnahme',
  control: 'Kontrolle',
  photo: 'Foto',
  user: 'Benutzer',
  lv: 'Leistungsverzeichnis',
  import: 'Import',
};

function getRelativeTime(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Gerade eben';
  if (mins < 60) return `Vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Vor ${days} Tag${days > 1 ? 'en' : ''}`;
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ActivityLog() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');

  // Filter-State
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'erfasser';
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filter-Optionen vom Backend
  const [filterOptions, setFilterOptions] = useState<{ users: any[]; actions: string[]; entity_types: string[] }>({
    users: [], actions: [], entity_types: [],
  });

  // Export-Dialog
  const [exportDialog, setExportDialog] = useState(false);
  const [exportCount, setExportCount] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  // Filter-Params bauen (für Liste, Count, Export)
  const buildParams = useCallback(() => {
    const p: any = {};
    if (search) p.search = search;
    if (filterAction) p.action = filterAction;
    if (filterEntityType) p.entity_type = filterEntityType;
    if (filterUserId) p.user_id = filterUserId;
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    return p;
  }, [search, filterAction, filterEntityType, filterUserId, fromDate, toDate]);

  // Audit-Log laden (mit Debounce für Suche)
  useEffect(() => {
    const debounce = setTimeout(() => {
      setLoading(true);
      api.get('/reports/audit-log', { params: { ...buildParams(), limit: 200 } })
        .then(res => { setLogs(res.data); setError(''); })
        .catch(() => setError('Historie konnte nicht geladen werden'))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(debounce);
  }, [buildParams]);

  // Filter-Optionen einmalig laden
  useEffect(() => {
    api.get('/reports/audit-log/filter-options')
      .then(res => setFilterOptions(res.data))
      .catch(() => {/* still */});
  }, []);

  const activeFilters = [
    filterAction, filterEntityType, filterUserId, fromDate, toDate,
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setSearch(''); setFilterAction(''); setFilterEntityType('');
    setFilterUserId(''); setFromDate(''); setToDate('');
  };

  // ── EXPORT ─────────────────────────────────────────────────────────────────
  const openExportDialog = async () => {
    setExportDialog(true);
    setExportCount(null);
    try {
      const res = await api.get('/reports/audit-log/count', { params: buildParams() });
      setExportCount(res.data.count);
    } catch {
      setExportCount(0);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/reports/audit-log/export', {
        params: buildParams(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setExportDialog(false);
      setSnackbar(`${exportCount} Einträge exportiert`);
    } catch {
      setSnackbar('Fehler beim Export');
    } finally {
      setExporting(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>Historie</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Alle Änderungen im System
      </Typography>

      {/* Toolbar: Suche + Filter + Export */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Suche: Aktion, Inhalt..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20 }} /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <Button
          size="small"
          variant={showFilters ? 'contained' : 'outlined'}
          startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
          color={activeFilters > 0 ? 'primary' : 'inherit'}
          sx={{ minWidth: 'auto' }}
        >
          Filter {activeFilters > 0 && `(${activeFilters})`}
        </Button>
        <Tooltip title="Aktuelle Filterung als CSV exportieren">
          <Button
            size="small"
            variant="outlined"
            startIcon={<Download />}
            onClick={openExportDialog}
            sx={{ minWidth: 'auto' }}
          >
            Export
          </Button>
        </Tooltip>
      </Box>

      {/* Filter-Panel (aufklappbar) */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ py: '12px !important' }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Aktion</InputLabel>
                  <Select value={filterAction} label="Aktion" onChange={e => setFilterAction(e.target.value)}>
                    <MenuItem value="">Alle</MenuItem>
                    {filterOptions.actions.map(a => (
                      <MenuItem key={a} value={a}>{ACTION_CONFIG[a]?.label || a}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Entität</InputLabel>
                  <Select value={filterEntityType} label="Entität" onChange={e => setFilterEntityType(e.target.value)}>
                    <MenuItem value="">Alle</MenuItem>
                    {filterOptions.entity_types.map(et => (
                      <MenuItem key={et} value={et}>{ENTITY_LABELS[et] || et}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {isAdmin && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Benutzer</InputLabel>
                  <Select value={filterUserId} label="Benutzer" onChange={e => setFilterUserId(e.target.value)}>
                    <MenuItem value="">Alle</MenuItem>
                    {filterOptions.users.map((u: any) => (
                      <MenuItem key={u.id} value={u.id}>{u.name || u.email}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              )}
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField
                  size="small" type="date" label="Von" fullWidth
                  value={fromDate} onChange={e => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={1.5}>
                <TextField
                  size="small" type="date" label="Bis" fullWidth
                  value={toDate} onChange={e => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            {activeFilters > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" onClick={clearAllFilters} startIcon={<Close />}>
                  Alle Filter zurücksetzen
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Collapse>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.82rem' }}>
        {logs.length} Einträge{logs.length >= 200 ? ' (Anzeige limitiert auf neueste 200, Export liefert alle)' : ''}
      </Typography>

      {loading ? (
        Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={70} sx={{ borderRadius: 2, mb: 1 }} />)
      ) : logs.length === 0 ? (
        activeFilters > 0 || search ? (
          <EmptyState
            icon={Search}
            title="Keine Einträge gefunden"
            description="Mit den aktuellen Filtern wurden keine Aktivitäten gefunden. Filter anpassen oder zurücksetzen."
            action={{ label: 'Alle Filter zurücksetzen', onClick: clearAllFilters }}
            accentColor="info"
          />
        ) : (
          <EmptyState
            icon={History}
            title="Noch keine Aktivitäten"
            description="Sobald Bäume, Schäden, Maßnahmen oder Kontrollen geändert werden, erscheinen die Einträge hier."
            accentColor="info"
          />
        )
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {logs.map((log: any) => {
            const cfg = ACTION_CONFIG[log.action] || { icon: <Edit sx={{ fontSize: 16 }} />, color: '#666', label: log.action };
            return (
              <Box key={log.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider',
                cursor: log.tree_id ? 'pointer' : 'default',
                '&:hover': log.tree_id ? { bgcolor: 'action.hover' } : {},
              }} onClick={() => log.tree_id && navigate(`/trees/${log.tree_id}`, { state: { from: 'activity' } })}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  bgcolor: cfg.color + '15', color: cfg.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {cfg.icon}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip label={cfg.label} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: cfg.color + '20', color: cfg.color }} />
                    {(log.action === 'TREE_BULK_DELETED' || log.action === 'TREE_BULK_FELLED') && (() => {
                      let nd: any = log.new_data;
                      if (typeof nd === 'string') { try { nd = JSON.parse(nd); } catch { nd = null; } }
                      const nums: string[] = nd && Array.isArray(nd.baumnummern) ? nd.baumnummern : [];
                      const count = nd?.count ?? nums.length;
                      const preview = nums.slice(0, 6).join(', ');
                      const rest = nums.length > 6 ? ` +${nums.length - 6} weitere` : '';
                      return (
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                          {count} Bäume{nums.length ? `: ${preview}${rest}` : ''}
                        </Typography>
                      );
                    })()}
                    {log.baumnummer && (
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.82rem',
                          // Highlight wenn Suchbegriff exakt der aktuellen Baumnummer entspricht (BUG-015)
                          ...(search && log.baumnummer.toLowerCase().includes(search.toLowerCase())
                            ? { bgcolor: 'warning.light', px: 0.6, borderRadius: 0.5 }
                            : {}),
                        }}
                      >
                        Baum #{log.baumnummer}
                      </Typography>
                    )}
                    {/* Alte Baumnummer (archive_baumnummer) immer anzeigen wenn vorhanden – BUG-015 */}
                    {log.archive_baumnummer && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.72rem',
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          // Highlight wenn Suche auf alte Nummer matcht
                          ...(search && log.archive_baumnummer.toLowerCase().includes(search.toLowerCase())
                            ? { bgcolor: 'warning.light', color: 'warning.contrastText', px: 0.6, borderRadius: 0.5, fontStyle: 'normal', fontWeight: 600 }
                            : {}),
                        }}
                      >
                        (vorher #{log.archive_baumnummer})
                      </Typography>
                    )}
                    {log.baumart && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                        {log.baumart}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.72rem' }}>
                    {log.user_name}
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontSize: '0.72rem' }}>
                  {getRelativeTime(log.created_at)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Export-Dialog */}
      <Dialog open={exportDialog} onClose={() => !exporting && setExportDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Audit-Log exportieren</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Exportiert die aktuell gefilterten Aktivitäten als CSV-Datei (Zeitstempel, Benutzer, Aktion, Entität, Baumnummer, alte und neue Daten).
          </Typography>
          {exportCount === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Anzahl wird ermittelt...</Typography>
            </Box>
          ) : (
            <Alert severity={exportCount > 5000 ? 'warning' : 'info'} icon={<Download />}>
              Diese Filterung umfasst <strong>{exportCount.toLocaleString('de-DE')}</strong> {exportCount === 1 ? 'Eintrag' : 'Einträge'}.
              {exportCount > 5000 && ' Das Erzeugen der Datei kann etwas dauern.'}
              {exportCount === 0 && ' Es gibt nichts zu exportieren.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)} disabled={exporting}>Abbrechen</Button>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <Download />}
            onClick={doExport}
            disabled={exporting || exportCount === null || exportCount === 0}
          >
            {exporting ? 'Wird erzeugt...' : 'Jetzt exportieren'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar('')}
        message={snackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
